import type { Subscription } from "@rails/actioncable";
import type Message from "@travily/types/models/MessagesMessage";
import { useCallback, useEffect, useRef, useState } from "react";
import { getConsumer } from "@/lib/cable";

/** One ping per this much continuous composing — a keystroke is not an event. */
const PING_INTERVAL_MS = 2000;
/** Outlives a ping by more than one interval, so a steady typist's bubble never blinks out. */
const EXPIRY_MS = 4000;

interface TypingPing {
  user_id: number;
}

export interface UseTypingIndicatorOptions {
  /** The viewer's user id: a ping carrying it is their own, from this tab or another of theirs. */
  currentUserId: number | undefined;
  /** The rendered thread — the peer's message landing retires the bubble that stood in for it. */
  messages: Message[] | undefined;
  /** Signed stream token. Null until a conversation exists; there is nothing to subscribe to yet. */
  stream: string | null;
}

export interface UseTypingIndicatorResult {
  isPeerTyping: boolean;
  /** For the composer's `onTyping`: throttled here, so the composer may fire it on every change. */
  onTyping: () => void;
}

/**
 * The counterpart's typing state on one thread, over ConversationTypingChannel. Transient in both
 * directions: a ping that finds nobody listening is simply lost.
 */
export function useTypingIndicator({
  currentUserId,
  messages,
  stream,
}: UseTypingIndicatorOptions): UseTypingIndicatorResult {
  const [isPeerTyping, setIsPeerTyping] = useState(false);
  const subscriptionRef = useRef<Subscription | null>(null);
  const expiryRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastPingAtRef = useRef(0);

  useEffect(() => {
    // The messages page swaps conversation without remounting, so a bubble from the thread being
    // left would otherwise sit over the thread being opened.
    setIsPeerTyping(false);
    if (!stream || currentUserId === undefined) return;

    const subscription = getConsumer().subscriptions.create(
      { channel: "ConversationTypingChannel", signed_stream_name: stream },
      {
        received({ user_id }: TypingPing) {
          // The server fans a ping out to its sender too — this account may have the dock and the
          // messages page open at once — so the client is where "not mine" is decided.
          if (user_id === currentUserId) return;
          setIsPeerTyping(true);
          if (expiryRef.current) clearTimeout(expiryRef.current);
          expiryRef.current = setTimeout(() => setIsPeerTyping(false), EXPIRY_MS);
        },
      },
    );
    subscriptionRef.current = subscription;

    return () => {
      subscription.unsubscribe();
      subscriptionRef.current = null;
      if (expiryRef.current) clearTimeout(expiryRef.current);
    };
  }, [currentUserId, stream]);

  const lastMessage = messages?.at(-1);
  const lastMessageId = lastMessage?.id;
  const lastSenderId = lastMessage?.sender_id;
  useEffect(() => {
    // The message the bubble stood in for has arrived; waiting out the expiry would leave the peer
    // typing underneath what they just said.
    if (lastMessageId === undefined || lastSenderId === currentUserId) return;
    if (expiryRef.current) clearTimeout(expiryRef.current);
    setIsPeerTyping(false);
  }, [currentUserId, lastMessageId, lastSenderId]);

  const onTyping = useCallback(() => {
    const now = Date.now();
    if (now - lastPingAtRef.current < PING_INTERVAL_MS) return;
    lastPingAtRef.current = now;
    subscriptionRef.current?.perform("typing");
  }, []);

  return { isPeerTyping, onTyping };
}
